// src/pages/PaymentSuccessPage/PaymentSuccessPage.tsx

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext'; 
import { API_BASE_URL } from '../../config'; 
import styles from './PaymentSuccessPage.module.css';

type IconType = 'loading' | 'success' | 'error' | 'lock';

const PaymentSuccessPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    // Auth Context
    const { user, token, isLoading: authLoading, updateUser } = useAuth(); 
    
    const [isValidSession, setIsValidSession] = useState(false);
    
    // Estado Visual
    const [currentIcon, setCurrentIcon] = useState<IconType>('loading');
    const [title, setTitle] = useState('Processando...');
    const [subtitle, setSubtitle] = useState('Aguarde enquanto verificamos seu pagamento.');

    const [downloadLoading, setDownloadLoading] = useState(false);
    const [emailLoading, setEmailLoading] = useState(false);
    const [statusMsg, setStatusMsg] = useState<{type: 'success'|'error', text: string} | null>(null);

    // 1. Efeito Principal: Validação da URL e Revalidação do Usuário
    useEffect(() => {
        const status = searchParams.get('payment_status');
        const action = searchParams.get('action');

        // SEGURANÇA: URL params sozinhos não provam pagamento — um atacante
        // poderia mandar um link com payment_status=paid&action=download_plugin
        // para phishing/engano. A UI de "Pagamento Confirmado" só aparece
        // após revalidateUserSession() confirmar com o backend que o plano
        // está realmente ativo (e o download em si já é gated por
        // /api/products/secure-download/* com token Bearer).
        if (status === 'paid' && action === 'download_plugin') {
            setIsValidSession(true);

            if (token) {
                revalidateUserSession();
            } else {
                // Sem token: precisa refazer login para provar identidade.
                setCurrentIcon('loading');
            }
        } else {
             navigate('/', { replace: true });
        }
    }, [searchParams, navigate, token]);

    // Função para buscar dados frescos do usuário (Novo Plano)
    const revalidateUserSession = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/user/me`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const updatedUser = await res.json();
                // Se o AuthContext tiver a função updateUser, atualizamos o estado global
                if (updateUser) {
                    updateUser(updatedUser);
                }
                
                // Sucesso visual
                setCurrentIcon('success');
                setTitle('Pagamento Confirmado!');
                setSubtitle('Seu plano foi ativado com sucesso. O Kitsync está pronto.');
            } else {
                // Se falhar a revalidação, ainda mostramos sucesso do pagamento, 
                // mas o download pode falhar se o token for velho.
                setCurrentIcon('success');
                setTitle('Pagamento Recebido');
                setSubtitle('Pode ser necessário refazer login para atualizar seu plano.');
            }
        } catch (error) {
            console.error("Erro ao revalidar sessão:", error);
            setCurrentIcon('success'); 
            setTitle('Concluído');
            setSubtitle('Se o download falhar, tente sair e entrar na conta novamente.');
        }
    };

    const renderIcon = () => {
        if (authLoading || (!isValidSession && currentIcon === 'loading')) {
            return <div className={styles.iconContainer}><div className={styles.spinner}></div></div>;
        }

        // Caso: Usuário não logado (Sessão Expirada) -> CADEADO
        if (!user) {
            return (
                <div className={styles.iconContainer}>
                    <svg viewBox="0 0 52 52" className={styles.lockIcon}>
                        <circle className={styles.lockCircle} cx="26" cy="26" r="25" fill="none" />
                        <path className={styles.lockPath} d="M 19 21 V 16 A 7 7 0 0 1 33 16 V 21" />
                        <rect className={styles.lockPath} x="16" y="21" width="20" height="17" rx="2.5" />
                        <path className={styles.lockPath} d="M 26 27 V 31" />
                    </svg>
                </div>
            );
        }

        switch (currentIcon) {
            case 'success':
                return <div className={styles.iconContainer}><svg viewBox="0 0 52 52" className={styles.checkmark}><circle className={styles.checkmarkCircle} cx="26" cy="26" r="25" fill="none" /><path className={styles.checkmarkCheck} fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" /></svg></div>;
            case 'error':
                return <div className={styles.iconContainer}><svg viewBox="0 0 52 52" className={styles.cross}><circle className={styles.crossCircle} cx="26" cy="26" r="25" fill="none" /><path className={styles.crossLine} fill="none" d="M16 16 36 36" /><path className={styles.crossLine} fill="none" d="M36 16 16 36" /></svg></div>;
            default:
                return <div className={styles.iconContainer}><div className={styles.spinner}></div></div>;
        }
    };

    const handleSecureDownload = async () => {
        if (!token) return;
        setDownloadLoading(true);
        setStatusMsg(null);
        try {
            // Chama a API segura (Gatekeeper vai verificar o plano)
            const res = await fetch(`${API_BASE_URL}/api/products/secure-download/kitsynk`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            
            if (res.ok && data.url) {
                // Inicia o download
                window.location.href = data.url;
            } else {
                // Tratamento de erro (ex: 403 Forbidden)
                const errorText = data.error || 'Acesso negado.';
                setStatusMsg({ type: 'error', text: errorText });
                
                if (res.status === 403) {
                     setSubtitle('Seu plano ainda não parece ativo. Tente fazer login novamente.');
                }
                setCurrentIcon('error');
                setTimeout(() => setCurrentIcon('success'), 4000);
            }
        } catch (error) {
            setStatusMsg({ type: 'error', text: 'Erro de conexão.' });
            setCurrentIcon('error');
            setTimeout(() => setCurrentIcon('success'), 3000);
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleSendEmail = async () => {
        if (!token) return;
        setEmailLoading(true);
        setStatusMsg(null);
        try {
            const res = await fetch(`${API_BASE_URL}/api/products/send-download-email`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) {
                setStatusMsg({ type: 'success', text: '✅ Link enviado para seu e-mail!' });
            } else {
                setStatusMsg({ type: 'error', text: data.error || 'Erro ao enviar e-mail.' });
            }
        } catch (error) {
            setStatusMsg({ type: 'error', text: 'Erro de conexão.' });
        } finally {
            setEmailLoading(false);
        }
    };

    if (authLoading || !isValidSession) return <div className={styles.fullscreenOverlay} style={{ background: '#131313' }} />;

    if (!user) {
         return (
             <div className={styles.fullscreenOverlay}>
                <div className={styles.card} style={{ maxWidth: '400px' }}>
                    {renderIcon()}
                    <h2 className={styles.title}>Sessão Expirada</h2>
                    <p className={styles.subtitle}>Para validar sua compra e baixar o arquivo, faça login novamente.</p>
                    <button onClick={() => navigate('/')} className={styles.downloadBtn}>IR PARA LOGIN</button>
                </div>
             </div>
        );
    }

    return (
        <div className={styles.fullscreenOverlay}>
            <div className={styles.card}>
                {renderIcon()}
                <h1 className={styles.title}>{currentIcon === 'error' ? 'Ops!' : title}</h1>
                <p className={styles.subtitle}>{currentIcon === 'error' ? 'Houve um problema com sua solicitação.' : subtitle}</p>
                
                {statusMsg && (
                    <div className={styles.statusBox} style={{ background: statusMsg.type === 'success' ? 'rgba(46, 204, 113, 0.1)' : 'rgba(231, 76, 60, 0.1)', color: statusMsg.type === 'success' ? '#2ecc71' : '#ff5252', border: statusMsg.type === 'success' ? '1px solid #2ecc71' : '1px solid #ff5252' }}>
                        {statusMsg.text}
                    </div>
                )}
                
                <div>
                    <button onClick={handleSecureDownload} className={styles.downloadBtn} disabled={downloadLoading || emailLoading || currentIcon === 'error'}>
                        {downloadLoading ? 'Gerando Link...' : (<><svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg> BAIXAR KITSINC (.ZIP)</>)}
                    </button>
                    
                    <button onClick={handleSendEmail} className={styles.emailBtn} disabled={downloadLoading || emailLoading || currentIcon === 'error'}>
                        {emailLoading ? 'Enviando...' : '📧 Enviar link de backup por e-mail'}
                    </button>
                </div>
                
                <button onClick={() => navigate('/plugins')} className={styles.homeLink}>Voltar para Meus Plugins</button>
            </div>
        </div>
    );
};

export default PaymentSuccessPage;